import {
  Component,
  computed,
  inject,
  OnInit,
  Output,
  EventEmitter,
  signal,
} from '@angular/core';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { I18nPipe } from '../../../shared/i18n/i18n-pipe';
import { I18nScopedService } from '../../../shared/i18n/i18n-scoped.service';
import { provideI18nScope } from '../../../shared/i18n/i18n-scope.provider';
import { I18nScopeReadyDirective } from '../../../shared/i18n/i18n-scope-ready.directive';
import { TimeSeriesTablesService } from '../../../features/timeseries-data/services/timeseries-tables.service';
import { TimeSeriesTablesUiService } from '../../timeseries/timeseries-tables-ui.service';
import { PermissionService } from '../../../shared/services/permissions/permission.service';
import { AccessLevel, Permissions } from '@anessa/shared-models';
import { ProjectService } from '../../../shared/services/project/project.service';
import { MyAuthService } from '../../../auth/auth.service';
import {
  SidebarPopout,
  type SidebarPopoutGroup,
  type SidebarPopoutItem,
} from '../../sidebar-popout/sidebar-popout';

@Component({
  selector: 'app-timeseries-popout',
  imports: [SidebarPopout, I18nPipe, I18nScopeReadyDirective],
  templateUrl: './timeseries-popout.html',
  providers: [provideI18nScope('ui/sidebar'), I18nScopedService],
})
export class TimeseriesPopout implements OnInit {
  private readonly router = inject(Router);
  private readonly tablesService = inject(TimeSeriesTablesService);
  private readonly timeseriesTablesUi = inject(TimeSeriesTablesUiService);
  private readonly projectService = inject(ProjectService);
  private readonly authService = inject(MyAuthService);
  private readonly i18nService = inject(I18nScopedService);
  private readonly permissionService = inject(PermissionService, {
    optional: true,
  });

  @Output() readonly popoutClose = new EventEmitter<void>();

  protected readonly activeTableId = signal<string | null>(null);
  protected readonly sortMode = signal<'group' | 'alpha'>('group');

  protected readonly canEdit = computed(() => {
    this.permissionService?.lastResolvedPlantId();
    return (
      this.permissionService?.hasPermission(Permissions.EDIT_TIMESERIES) ??
      false
    );
  });

  protected readonly canAddTable = computed(
    () =>
      this.canEdit() &&
      !(this.projectService.currentProject()?.isMultiPlant ?? false),
  );

  protected readonly groups = computed<SidebarPopoutGroup[]>(() => {
    const visibleTables = this.tablesService.tables().filter((t) => t.visible);

    if (this.sortMode() === 'alpha') {
      return this.buildAlphaGroups(visibleTables);
    }
    return this.buildGroupGroups(visibleTables);
  });

  constructor() {
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((e) => this.syncActiveTable(e.urlAfterRedirects));
  }

  ngOnInit(): void {
    this.syncActiveTable(this.router.url);
  }

  private buildItem(
    table: {
      id: string;
      name: string;
      userId: string;
      accessLevel?: AccessLevel;
    },
    ownerTooltip: string,
    currentUserId: string | undefined,
  ): SidebarPopoutItem {
    const isOwner = !!currentUserId && table.userId === currentUserId;
    return {
      id: table.id,
      label: table.name,
      labelIcon: isOwner ? 'person' : undefined,
      labelIconTooltip: isOwner ? ownerTooltip : undefined,
      showAction: table.accessLevel === 'admin',
      actionIcon: 'share',
      actionTooltip: this.i18nService.t('share'),
    };
  }

  private buildGroupGroups(
    tables: {
      id: string;
      name: string;
      group: string;
      order: number;
      userId: string;
      accessLevel?: AccessLevel;
    }[],
  ): SidebarPopoutGroup[] {
    const currentUserId = this.authService.ensuredUser()?._id;
    const ownerTooltip = this.i18nService.t('owner_icon_tooltip');
    const groupMap = new Map<string, SidebarPopoutItem[]>();

    for (const table of [...tables].sort((a, b) => a.order - b.order)) {
      const key = table.group || '—';
      const items = groupMap.get(key) ?? [];
      items.push(this.buildItem(table, ownerTooltip, currentUserId));
      groupMap.set(key, items);
    }

    return Array.from(groupMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, items]) => ({ key, items }));
  }

  private buildAlphaGroups(
    tables: {
      id: string;
      name: string;
      order: number;
      userId: string;
      accessLevel?: AccessLevel;
    }[],
  ): SidebarPopoutGroup[] {
    const currentUserId = this.authService.ensuredUser()?._id;
    const ownerTooltip = this.i18nService.t('owner_icon_tooltip');
    const groupMap = new Map<string, SidebarPopoutItem[]>();

    for (const table of [...tables].sort((a, b) =>
      a.name.localeCompare(b.name),
    )) {
      const key = table.name[0]?.toUpperCase() ?? '?';
      const items = groupMap.get(key) ?? [];
      items.push(this.buildItem(table, ownerTooltip, currentUserId));
      groupMap.set(key, items);
    }

    return Array.from(groupMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, items]) => ({ key, items }));
  }

  private syncActiveTable(url: string): void {
    const match = /\/timeseries-data\/([^/?]+)/.exec(url);
    this.activeTableId.set(match ? decodeURIComponent(match[1]) : null);
  }

  protected toggleSortMode(): void {
    this.sortMode.update((m) => (m === 'group' ? 'alpha' : 'group'));
  }

  protected onItemSelect(id: string): void {
    void this.router.navigate(['/timeseries-data', id]);
  }

  protected addTable(): void {
    this.timeseriesTablesUi.openAddTableDialog();
  }

  protected openSectionManager(): void {
    this.timeseriesTablesUi.openSectionManagerDialog();
  }

  protected onTableShare(id: string): void {
    const table = this.tablesService.getTableById(id);
    if (!table) return;
    void this.timeseriesTablesUi.openShareDialog(table);
  }
}
